<template>
  <v-card height="100%" class="d-flex flex-column">
    <v-card-text v-if="allocation" style="overflow:scroll; height:100%">

      <v-row class="mb-0 mt-2">
        <v-col class="headline font-weight-bold" cols="8">
          Allocation Form {{ allocation ? `#${allocation.id}` : '' }} 
          <span class="text-subtitle-1 ml-4">
            {{ allocation.job.code }} - {{ allocation.job.project.name }}; {{ allocation.job.reference }}
          </span>
        </v-col>
        <v-col cols="4" class="text-right">
          <span class="text-subtitle-1 mr-2">Status:</span>
          <v-chip :color="statusMap[allocation.status_id].color" :text-color="statusMap[allocation.status_id].text_color" style="text-transform: uppercase;">
            {{ statusMap[allocation.status_id].name }}
          </v-chip>
        </v-col>
      </v-row>

      <div v-if="allocation">
        <v-row>
          <v-col cols="8">
            <span v-if="allocation.user" class="mr-4">
              Created By {{ allocation.user.first_name }} {{ allocation.user.last_name }}
            </span>
            <span cols="2">
              Created At {{ allocation.created_at }}
            </span>
          </v-col>
          <v-col v-if="bom.import_errors.length > 0" cols="4" class="text-h5 text-right font-weight-bold">
            <v-btn color="error darken-1" @click="redirectToBom">
              <v-icon left>
                mdi-exclamation-thick
              </v-icon>
              {{ bom.import_errors.length }} Item(s) failed to import
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col cols="2">
            <v-menu ref="menuAllocateBy" v-model="showMenuAllocateBy" transition="scale-transition" offset-y :close-on-click="false" :close-on-content-click="false" min-width="auto">
              <template #activator="{ on: { click }, attrs }">
                <v-text-field
                  :value="`${allocation.allocate_by_date || ''} ${allocation.allocate_by_time || ''}`"
                  @click="e => openAllocateOverlay(click, e)"
                  v-bind="attrs"
                  readonly
                  dense
                  hide-details
                  :disabled="!canEdit"
                  append-icon="mdi-calendar"
                >
                  <template #label>Allocate By<red-asterisk /></template>
                </v-text-field>
              </template>
              <v-card>
                <v-card-title>
                  Allocate By
                </v-card-title>
                <v-card-text>
                  <v-date-picker v-model="allocation.allocate_by_date" scrollable :disabled="!canEdit" class="mr-1"/>
                  <v-time-picker v-model="allocation.allocate_by_time" format="24hr" scrollable :disabled="!canEdit" class="ml-1"/>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn small outlined @click="showMenuAllocateBy = false">Ok</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>

          <v-col cols="2">
            <v-menu ref="menuSupplyBy" v-model="showMenuSupplyBy" transition="scale-transition" :close-on-click="false" :close-on-content-click="false" offset-y min-width="auto">
              <template #activator="{ on: { click }, attrs }">
                <v-text-field
                  :value="`${allocation.supply_by_date || ''} ${allocation.supply_by_time || ''}`"
                  @click="e => openSupplyOverlay(click, e)"
                  v-bind="attrs"
                  readonly
                  dense
                  hide-details
                  :disabled="!canEdit"
                  append-icon="mdi-calendar"
                >
                  <template #label>Supply By</template>
                </v-text-field>
              </template>
              <v-card>
                <v-card-title>
                  Supply By
                </v-card-title>
                <v-card-text>
                  <v-date-picker v-model="allocation.supply_by_date" scrollable :disabled="!canEdit" class="mr-1"/>
                  <v-time-picker v-model="allocation.supply_by_time" format="24hr" scrollable :disabled="!canEdit" class="ml-1"/>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn small outlined @click="showMenuSupplyBy = false">Ok</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>

          <v-col class="text-right">
            <v-btn v-if="!showItemForm && canEdit" color="primary" outlined @click="openItemForm">
              <v-icon small class="mr-1">
                fas fa-plus
              </v-icon>
              New Item
            </v-btn>
          </v-col>
        </v-row>

      </div>

      <v-dialog v-model="showItemForm" persistent width="50%">
        <v-card>
          <v-card-title>
            New Item
          </v-card-title>
          <v-card-text>
            <v-row class="mt-2">
              <v-col cols="6">
                <inventory-class-select-element v-model="itemClassId"/>
              </v-col>
              <v-col cols="6">
                <v-autocomplete 
                  v-model="itemSubclassIds" 
                  label="Sub Classes"
                  :items="filteredSubClassesList"
                  item-text="name"
                  item-value="id"
                  multiple
                >
                  <template #item="{ item }">
                    <v-chip
                      class="mt-1 mb-1"
                      small
                      text-color="white"
                      style="cursor: pointer;"
                      :color="item ? item.color : 'white'"
                    >
                      {{ item ? item.name : '' }}
                    </v-chip>
                  </template>
                  <template #selection="{ item }">
                    <v-chip
                      class="mt-1 mb-1"
                      small
                      text-color="white"
                      style="cursor: pointer;"
                      :color="item ? item.color : 'white'"
                    >
                      {{ item ? item.name : '' }}
                    </v-chip>
                  </template>
              </v-autocomplete>
              </v-col>
            </v-row>
            <v-row class="mt-2">
              <v-col cols="8">
                <v-autocomplete 
                  v-model="newItem.inventory_item_id" 
                  :items="inventoryItems" 
                  item-value="id" 
                  :item-text="item => `${item.material_id}: ${item.name}`" 
                  hide-details 
                  class="pt-0"
                >
                  <template #label>Inventory Item<red-asterisk/></template>
                  <template #item="{ item }">
                    <span class="font-weight-bold">{{ item.material_id }}</span>:
                    {{ item.name }}
                  </template>
                  <template #selection="{ item }">
                    <span class="font-weight-bold">{{ item.material_id }}</span>:
                    {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="4">
                <v-autocomplete 
                  v-model="newItem.color_id" 
                  label="Color" 
                  :items="colors" 
                  item-value="id" 
                  hide-details 
                  class="pt-0">
                  <template #item="{ item }">
                    {{ item.code }}: {{ item.name }}
                  </template>
                  <template #selection="{ item }">
                    {{ item.code }}: {{ item.name }}
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-autocomplete v-model="newItem.uom_id" :items="itemUoms" item-value="id" item-text="name" hide-details class="pt-0">
                  <template #label>UOM<red-asterisk/></template>
                </v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="newItem.qty_required" hide-details class="pt-0">
                  <template #label>Quantity Required<red-asterisk/></template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="closeItemForm" color="info" outlined class="mr-2">CLOSE</v-btn>
            <v-btn @click="addItem" color="success" outlined :loading="itemIsBeingAdded">ADD</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-card outlined>
            <v-data-table
              v-if="isInitialized"
              :headers="allocationItemsHeaders"
              :items="allocation.items"
              hide-default-footer
              :sortable="false"
              :items-per-page="-1"
              dense
            >
              <template #item="{ item }">
                <tr 
                  v-if="!item.is_deleted" 
                  class="py-1"
                  :class="{ 'red lighten-5': item.bom_is_deleted, 'orange lighten-5': item.bom_is_changed }"
                >
                  <td class="text-center">{{ item.inventory_item.material_id || '' }}</td>
                  <td>
                    <v-img 
                      v-if="(item.inventory_item_id in extraDataMap) && extraDataMap[item.inventory_item_id].inventory_item_photo" 
                      @click="openItemShowImageForm(item)"
                      :src="`data:${extraDataMap[item.inventory_item_id].inventory_item_photo.photo_mime};base64, ${extraDataMap[item.inventory_item_id].inventory_item_photo.photo}`" 
                      style="width:50px"
                      />
                  </td>
                  <td class="text-center">{{ item.inventory_item.name || '' }}</td>
                  <td class="text-center">{{ item.color ? item.color.name : '' }}</td>
                  <td class="text-center">{{ item.uom ? item.uom.name : '' }}</td>
                  <td class="text-center">{{ item.length ? item.length.name : '' }}</td>
                  <td class="text-center" style="border-right:thin solid rgba(0, 0, 0, 0.12)">{{ Number(item.qty_required) }}</td>
                  <td class="text-center">{{ getAvailableQty(item) }}/{{ getOrderedQty(item) }}</td>
                  <td class="text-center">{{ getAllocatedQty(item)}}</td>
                  <td class="text-center">{{ Number(extraDataMap[item.inventory_item_id].qty_supplied) }}</td>
                  <td>
                    <div v-for="(tag, i) in item.tags.filter(x => !x.is_deleted)" :key="`tag-${i}`">
                      <v-row class="my-0" :class="{ 'pt-3': i === 0 }">
                        <v-col cols="1" class="pb-0 pt-1 pr-1">
                          <v-btn 
                            v-if="i === 0 && canEdit"
                            color="success" 
                            outlined 
                            tile 
                            x-small
                            style="border-radius: 7px; width: 28px !important; height: 28px !important;" 
                            icon 
                            @click="addQr(item)">
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col cols="8" class="pt-0 pr-1" :class="{ 'pb-4': i === item.tags.length - 1 }">
                          <div v-if="tag.id">
                            <v-text-field 
                              disabled 
                              dense 
                              :value="getTagName(tag)" 
                              label="QR/UPC" 
                              hide-details 
                            />
                            <div v-if="Number(tag.released_qty)">
                              Released: {{ Number(tag.released_qty) }}
                            </div>
                          </div>
                          <div v-else>
                            <v-select
                              clearable 
                              dense 
                              v-model="tag.inventory_tag" 
                              :items="extraDataMap[item.inventory_item_id].available_tags" 
                              item-title="inventory_tag" 
                              label="QR/UPC" 
                              hide-details
                              :disabled="!canEdit"
                              @change="onTagChanged(item, tag)">
                              <template #item="{item}">
                                {{ item.inventory_tag }}, 
                                <span class="ml-1">{{ item.shelf }},</span>
                                <span class="ml-1" style="color: green">Qty:</span> {{ tagQtyAvailableMap[item.inventory_tag] || 0 }}
                              </template>
                              <template #selection="{item}">
                                {{ item.inventory_tag }}, 
                                <span class="ml-1">{{ item.shelf }},</span>
                                <span class="ml-1" style="color: green">Qty:</span> {{ tagQtyAvailableMap[item.inventory_tag] || 0 }}
                              </template>
                            </v-select>
                            <div v-if="Number(tag.released_qty)">
                              Released: {{ Number(tag.released_qty) }}
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="2" class="pt-0 px-1" :class="{ 'pb-4': i === item.tags.length - 1 }">
                          <v-text-field v-model.number="tag.qty" 
                            dense 
                            label="QTY" 
                            hide-details
                            type="number"
                            :disabled="!canEdit"
                            :error="!tagQtyIsValid(tag)"
                            @change="onTagQtyChanged(item, tag)" />
                        </v-col>
                        <v-col cols="1" class="pb-0 pl-1 pt-1">
                          <v-btn 
                            v-if="(tag.id || i !== 0) && canEdit"
                            :disabled="false"
                            color="error" 
                            outlined 
                            x-small
                            tile 
                            style="border-radius: 7px; width: 28px !important; height: 28px !important;" 
                            icon 
                            @click="onClickRemoveQr(item, tag)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                  <td>
                    <v-row class="my-0">
                      <v-col cols="8" class="pr-1">
                        <v-select 
                          clearable 
                          dense 
                          :label="`Inventory PO`"
                          v-model="item.inventory_po_item_id"
                          :items="extraDataMap[item.inventory_item_id].available_po_items" 
                          item-value="id" 
                          hide-details
                          :disabled="!canEdit"
                          @change="onPoChange(item)">
                            <template #item="{item}">
                              PO# {{ item.po_number }}, 
                              <span class="ml-1" style="color: green">Qty:</span> {{ item.qty }}
                            </template>
                            <template #selection="{item}">
                              PO# {{ item.po_number }}
                            </template>
                            <template #append>
                              <v-icon @click="redirectToPO()">mdi-plus</v-icon>
                            </template>
                            <template #append-outer>
                              <v-icon @click="refreshPOItems(item)" class="spin-timer" :style="{ 'transform': `rotate(${poLoadingRotation[item.inventory_item_id] || 0}turn)` }">mdi-refresh</v-icon>
                            </template>
                        </v-select>
                        <div>
                          {{ getPoItemStatus(item) }}
                        </div>
                      </v-col>
                      <v-col cols="4" class="pl-1">
                        <v-text-field 
                          v-model.number="item.inventory_po_item_qty" 
                          dense 
                          :disabled="!canEdit"
                          label="QTY" 
                          @change="onPoQtyUpdated(item)"
                          :error="!poQtyIsValid(item)"
                          hide-details
                          type="number" />
                      </v-col>
                    </v-row>
                  </td>
                  <td>
                    <v-menu offset-y v-if="canEdit">
                      <template #activator="{ on, attrs }">
                        <v-btn class="ml-1" elevation="0" fab outlined x-small v-bind="attrs" tile color="primary" style="border-radius: 7px; width: 28px !important; height: 28px !important;" v-on="on">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list class="pa-3">

                        <v-list-item @click="deleteItem(item)">
                          <v-list-item-title :style="`color: red; font-weight: 600;`">
                            <v-row align="center">
                              <v-col cols="auto" class="pr-0" style="white-space: nowrap;">
                                <v-icon style="font-size: 16px;" color="error">fas fa-trash</v-icon>
                              </v-col>
                              <v-col class="pt-4 text-color-error">DELETE</v-col>
                            </v-row>
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </td>
                </tr>
              </template>

              <template slot="body.append">
                <tr class="footer">
                  <th colspan="6" class="text-right">Totals:</th>

                  <th class="text-center">{{ totalRequiredQty }}</th>
                  <th class="text-center">{{ totalAvailableQty }}/{{ totalOrderedQty }}</th>
                  <th class="text-center">{{ totalAllocatedQty }}</th>
                  <th class="text-center">{{ totalSuppledQty }}</th>

                  <th />
                  <th />
                  <th />
                </tr>
              </template>

            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <div class="pa-6 mt-3" style="background-color:#CFD8DC; border: thin solid rgba(0,0,0,0.12)">
        <v-row>
          <v-col cols="12" class="pb-0 font-weight-bold" style="font-size:16px;color:black">BOM UPDATES</v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-data-table
                v-if="isInitialized"
                :headers="bomChangesHeaders"
                :items="bomChanges"
                hide-default-footer
                :items-per-page="-1"
                dense
              >
                <template #item="{ item }">
                  <tr
                    v-if="!item.ignore"
                    :class="{ 'red lighten-5': item.bom_is_deleted, 'green lighten-5': item.bom_is_new, 'orange lighten-5': item.bom_is_changed }"
                  >
                    <td class="text-center">{{ item.inventory_item.material_id || '' }}</td>
                    <td class="text-center">{{ item.inventory_item.name || '' }}</td>
                    <td class="text-center">{{ item.color ? item.color.name : '' }}</td>
                    <td class="text-center">{{ item.uom ? item.uom.name : '' }}</td>
                    <td class="text-center">{{ item.length ? item.length.name : '' }}</td>
                    <td class="text-center">{{ Number(item.quantity) }}</td>
                    <td class="text-center">{{ item.reason || '' }}</td>
                    <td class="text-right">
                      <div v-if="canEdit">
                        <v-btn v-if="item.bom_is_new" color="primary" @click="addBomChangeToAllocation(item)" style="min-width:90px" outlined small class="py-1">ADD TO ALLOCATION</v-btn>
                        <v-btn v-if="item.bom_is_changed" color="primary" @click="updateBomChangesInAllocation(item)" style="min-width:90px" outlined small class="py-1">UPDATE ALLOCATION ITEM</v-btn>
                        <v-btn v-if="item.bom_is_deleted" color="primary" @click="removeBomChangeFromAllocation(item)" style="min-width:90px" outlined small class="py-1">DELETE FROM ALLOCATION</v-btn>
                      </div>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div class="pa-6 mt-3" style="background-color:#FFFDE7; border: thin solid rgba(0,0,0,0.12)">
        <v-row>
          <v-col cols="12" class="pb-0 font-weight-bold" style="font-size:16px;color:black">OVERSUPPLY</v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-data-table
                v-if="isInitialized"
                :headers="oversupplyItemsHeaders"
                :items="oversupplyItems"
                hide-default-footer
                :items-per-page="-1"
                dense
              >
                <template #item="{ item }">
                  <tr>
                    <td class="text-center">
                      <span v-if="item.request_id">

                    <div v-for="(tag, i) in item.tags.filter(x => !x.is_deleted)" :key="`tag-${i}`">
                      <v-row class="my-0" :class="{ 'pt-3': i === 0 }">
                        <v-col cols="1" class="pb-0 pt-1 pr-1">
                          <v-btn 
                            v-if="i === 0 && item.status_id === 1"
                            color="success" 
                            outlined 
                            tile 
                            x-small
                            style="border-radius: 7px; width: 28px !important; height: 28px !important;" 
                            icon 
                            @click="addQr(item)">
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col cols="7" class="pt-0 pr-1" :class="{ 'pb-4': i === item.tags.length - 1 }">
                          <div v-if="tag.id">
                            <v-text-field
                              disabled 
                              dense 
                              :value="getTagName(tag)" 
                              label="QR/UPC" 
                              hide-details 
                            />
                            <div v-if="Number(tag.released_qty)">
                              Released: {{ Number(tag.released_qty) }}
                            </div>
                          </div>
                          <div v-else>
                            <v-select
                              clearable 
                              dense 
                              :disabled="item.status_id !== 1"
                              v-model="tag.inventory_tag" 
                              :items="extraDataMap[item.inventory_item_id].available_tags" 
                              item-text="inventory_tag" 
                              label="QR/UPC" 
                              hide-details
                              @change="onTagChanged(item, tag)">
                              <template #item="{item}">
                                {{ item.inventory_tag }}, 
                                <span class="ml-1">{{ item.shelf }},</span>
                                <span class="ml-1" style="color: green">Qty:</span> {{ tagQtyAvailableMap[item.inventory_tag] || 0 }}
                              </template>
                              <template #selection="{item}">
                                {{ item.inventory_tag }}, 
                                <span class="ml-1">{{ item.shelf }},</span>
                                <span class="ml-1" style="color: green">Qty:</span> {{ tagQtyAvailableMap[item.inventory_tag] || 0 }}
                              </template>
                            </v-select>
                            <div v-if="Number(tag.released_qty)">
                              Released: {{ Number(tag.released_qty) }}
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="3" class="pt-0 px-1" :class="{ 'pb-4': i === item.tags.length - 1 }">
                          <v-text-field v-model.number="tag.qty" 
                            dense 
                            label="QTY" 
                            hide-details
                            type="number"
                            :disabled="item.status_id !== 1"
                            :error="!tagQtyIsValid(tag)"
                            @change="onTagQtyChanged(item, tag)" />
                        </v-col>
                        <v-col cols="1" class="pb-0 pl-1 pt-1">
                          <v-btn 
                            v-if="(tag.id || i !== 0) && item.status_id === 1"
                            :disabled="false"
                            color="error" 
                            outlined 
                            x-small
                            tile 
                            style="border-radius: 7px; width: 28px !important; height: 28px !important;" 
                            icon 
                            @click="onClickRemoveQr(item, tag)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                      </span>
                      <span v-else>
                        {{ item.inventory_tag }}
                      </span>
                    </td>
                    <td>
                      
                    <v-row v-if="item.request_id" class="my-0">
                      <v-col cols="7" class="pr-1">
                        <v-select 
                          clearable 
                          dense 
                          label="Inventory PO"
                          v-model="item.inventory_po_item_id"
                          :items="extraDataMap[item.inventory_item_id].available_po_items" 
                          item-value="id" 
                          hide-details
                          :disabled="item.status_id !== 1"
                          @change="onPoChange(item)">
                            <template #item="{item}">
                              PO# {{ item.po_number }}, 
                              <span class="ml-1" style="color: green">Qty:</span> {{ item.qty }}
                            </template>
                            <template #selection="{item}">
                              PO# {{ item.po_number }}
                            </template>
                            <template #append>
                              <v-icon @click="redirectToPO()">mdi-plus</v-icon>
                            </template>
                            <template #append-outer>
                              <v-icon @click="refreshPOItems(item)" class="spin-timer" :style="{ 'transform': `rotate(${poLoadingRotation[item.inventory_item_id] || 0}turn)` }">mdi-refresh</v-icon>
                            </template>
                        </v-select>

                      </v-col>
                      <v-col cols="5" class="pl-1">
                        <v-text-field 
                          v-model.number="item.inventory_po_item_qty" 
                          dense 
                          :disabled="item.status_id !== 1"
                          label="QTY" 
                          @change="onPoQtyUpdated(item)"
                          :error="!poQtyIsValid(item)"
                          hide-details
                          type="number" />
                      </v-col>
                    </v-row>
                      
                    </td>
                    <td class="text-center">{{ item.material_id }}</td>
                    <td class="text-center">{{ item.inventory_name }}</td>
                    <td class="text-center">{{ item.uom_name }}</td>
                    <td class="text-center">{{ item.length_name }}</td>
                    <td class="text-center">{{ item.request_id ? Number(item.qty_required) : '' }}</td>
                    <td class="text-center">{{ item.request_id ? Number(item.qty_supplied) : Number(item.qty_oversupplied) }}</td>
                    <td class="text-center">
                      {{ item.reason_name }}
                      <span v-if="item.sub_reason_name">: {{ item.sub_reason_name }}</span>
                    </td>
                    <td class="text-center">
                      <v-tooltip v-if="item.note" top color="black">
                        <template #activator="{ on }">
                          <v-icon v-on="on" x-small @click="openOversupplyNote(item)">mdi-eye</v-icon>
                        </template>
                        {{ item.note }}
                      </v-tooltip>
                    </td>
                    <td class="text-center">
                      <v-chip v-if="item.status_id in materialRequestStatusMap" small text-color="white" :color="materialRequestStatusMap[item.status_id].color">
                        <span style="text-transform:uppercase">{{ materialRequestStatusMap[item.status_id].name }}</span>
                      </v-chip>
                      <v-chip v-else-if="item.is_acknowledged" small color="success">
                        ACKNOWLEDGED
                      </v-chip>
                    </td>
                    <td class="text-right">
                      <div v-if="item.request_id">
                        <v-btn v-if="item.status_id === 1" @click="approveMaterialRequest(item)" color="success" style="min-width:90px" outlined small class="py-1">APPROVE</v-btn>
                        <v-btn v-if="item.status_id === 1" @click="declineMaterialRequest(item)" color="error" style="min-width:90px" outlined small class="py-1">REJECT</v-btn>
                      </div>
                      <span v-else>
                        <v-btn v-if="!item.is_acknowledged" @click="acknowledgeOversupply(item)" color="#FF8237" outlined small class="py-1">ACKNOWLEDGE</v-btn>
                      </span>

                    </td>
                  </tr>
                </template>

                <template slot="body.append">
                  <tr class="footer">
                    <th colspan="6" class="text-right">Totals:</th>

                    <th class="text-center">{{ totalOversupplyReqQty }}</th>
                    <th class="text-center">{{ totalOversuppliedQty }}</th>

                    <th colspan="4"></th>
                  </tr>
                </template>
              </v-data-table>

            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-row class="mt-12">
        <v-col v-if="allocation" cols="12">
          <v-textarea v-model="allocation.note" outlined rows="3" label="Note" :disabled="!canEdit" />
        </v-col>
      </v-row>

      <div v-if="bom">
        <div v-if="bom.import_errors.length > 0">
          <v-row>
            <v-col cols="12">
              <div class="text-h6">
                <v-btn small icon @click="showErrorsTable = !showErrorsTable">
                  <v-icon>{{ showErrorsTable ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                </v-btn>
                <span class="text-h6">Item(s) Failed To Import</span>
              </div>

                <v-data-table
                  v-if="isInitialized && showErrorsTable"
                  class="text-red"
                  :headers="errorItemsHeaders"
                  :items="bom.import_errors"
                  hide-default-footer
                  :items-per-page="-1"
                  dense
                >
                  <template #item="{ item }">
                    <tr>
                      <td class="text-center">{{ item.line_number }}</td>
                      <td class="text-center">{{ item.material_id }}</td>
                      <td class="text-center">{{ item.class }}</td>
                      <td class="text-center">{{ item.sub_class }}</td>
                      <td class="text-center">{{ item.work_order }}</td>
                      <td class="text-center">{{ item.description }}</td>
                      <td class="text-center">{{ item.ext_color_code }}</td>
                      <td class="text-center">{{ item.ext_color_description }}</td>
                      <td class="text-center">{{ item.int_color_code }}</td>
                      <td class="text-center">{{ item.int_color_description }}</td>
                      <td class="text-center">{{ Number(item.qty) }}</td>
                      <td class="text-center">{{ item.bar_length }}</td>
                      <td class="text-center">{{ item.category }}</td>
                      <td class="text-center">{{ item.reason }}</td>
                    </tr>
                  </template>
                  <template #footer>
                    <tr>
                    </tr>
                  </template>
                </v-data-table>
            </v-col>
          </v-row>
        </div>
        <v-row>
          <v-col>
            {{ bom.items.length }} Item(s) Imported Successfully / {{ bom.import_errors.length }} Item(s) Failed
          </v-col>
        </v-row>
      </div>


      <InventoryItemShowImageForm
        ref="itemShowImageForm"
      />

      <v-dialog v-model="showOversupplyNote" width="50%">
        <v-card v-if="oversupplyRecord">
          <v-card-title class="header">
            Oversupply Note For Tag: {{ oversupplyRecord.inventory_tag }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-card-text style="max-height: 600px; overflow-y: auto; border: 1px solid #d3d3d3; border-radius: 3px;">
                  <div>{{ oversupplyRecord.note }}</div>
                  <div>{{ oversupplyRecord.created_at }}</div>
                  <div>{{ oversupplyRecord.created_by_name }}</div>
                </v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="normal" class="mr-2" @click="closeOversupplyNote">CLOSE</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <confirm-form ref="confirmForm" />

      <v-row>
        <v-col>
          <v-btn v-if="allocation.bom_id" @click="redirectToBom" outlined color="primary">BOM</v-btn>
          <v-btn v-if="allocation.is_issued && !allocation.is_locked" @click="releaseAllocation" color="primary" class="ml-2" outlined>Release And Archive</v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn color="normal" class="mr-2" @click="close">
        CLOSE
      </v-btn>
      <v-btn v-if="canEdit" color="primary" @click="submitAllocation">
        SUBMIT
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as allocationApi from '../../../api/InventoryAllocationApi';
import * as bomApi from '../../../api/BomApi';
import InventoryItemShowImageForm from '../InventoryItemShowImageForm.vue';
import RedAsterisk from '../../RedAsterisk.vue';
import InventoryClassSelectElement from '../../elements/InventoryClassSelectElement.vue';

export default {
  components: {
    InventoryItemShowImageForm,
    RedAsterisk,
    InventoryClassSelectElement
  },
  emits: ['close'],
  data: () => ({
    isInitialized: false,

    allocationItemsHeaders: [
      { text: 'Item Code', align: 'center', sortable: false, class: 'px-0', width: '5%' },
      { text: 'Picture', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Item Name', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Colour', align: 'center', sortable: false, class: 'px-0', width: '10%' },
      { text: 'UOM', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Length', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Qty Needed', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Qty Available/Ordered', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Qty Allocated', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Qty Supplied', align: 'center', sortable: false, class: 'px-0' },
      { text: 'QR/UPC', align: 'center', sortable: false, class: 'px-0', width: '27%' },
      { text: 'Inventory PO', align: 'center', sortable: false, class: 'px-0', width: '18%' },
      { text: 'Actions', align: 'center', sortable: false, class: 'pl-0' },
    ],
    allocation: null,
    bom: null,
    extraDataMap: {},
    itemTagQtyAvailableMap: {}, //TODO fetch this from the DB?
    itemPoQtyAvailableMap: {}, //TODO fetch this from the DB?
    tagQtyAvailableMap: {},
    poQtyAvailableMap: {},
    showMenuAllocateBy: false,
    showMenuSupplyBy: false,
    showItemForm: false,
    statusMap: {},
    showErrorsTable: false,
    itemClassId: null,
    itemSubclassIds: null,
    filteredSubClassesList: [],
    newItem: {
      qty_required: null,
      inventory_item_id: null,
      uom_id: null,
      color_id: null,
    },
    itemIsBeingAdded: false,
    inventoryItems: [],
    colors: [],
    uoms: [],
    originalPoSelectionMap: {},
    subClassesList: [],
    materialRequestStatusMap: {},
    classToSubclassesMap: {},
    poLoadingRotation: {},
    oversupplyItemsHeaders: [
      { text: 'QR Code', align: 'center', sortable: false, width: '20%' },
      { text: 'Inv. PO', align: 'center', sortable: false, width: '20%' },
      { text: 'Item Code', align: 'center', sortable: false },
      { text: 'Item Name', align: 'center', sortable: false },
      { text: 'UOM', align: 'center', sortable: false },
      { text: 'Length', align: 'center', sortable: false },
      { text: 'Qty Required', align: 'center', sortable: false },
      { text: 'Qty Oversupplied', align: 'center', sortable: false },
      { text: 'Reason', align: 'center', sortable: false },
      { text: 'Note', align: 'center', sortable: false },
      { text: 'Status', align: 'center', sortable: false },
      { text: 'Actions', align: 'right', sortable: false },
    ],
    oversupplyItems: [],

    errorItemsHeaders: [
      { text: 'Line Number', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Material Id', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Class', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Sub Class', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Work Order', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Description', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Ext Color Code', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Ext Color Description', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Int Color Code', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Int Color Description', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Quantity', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Bar Length', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Category', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Reason', align: 'right', sortable: false, class: 'pl-0' },
    ],
    showOversupplyNote: false,
    oversupplyRecord: null,

    bomChangesHeaders: [
      { text: 'Item Code', align: 'center', sortable: false, class: 'px-0', width: '5%' },
      { text: 'Item Name', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Colour', align: 'center', sortable: false, class: 'px-0', width: '10%' },
      { text: 'UOM', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Length', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Qty Needed', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Reason', align: 'center', sortable: false, class: 'px-0' },
      { text: 'Actions', align: 'right', sortable: false, class: 'pl-0' },
    ],
    bomChanges: [],
  }),
  computed: {
    ...mapState({
      search: (state) => state.search,
      user: (state) => state.user,
    }),
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
    canEdit() {
      return !this.allocation?.is_issued;
    },
    totalRequiredQty() {
      if (!this.allocation || this.allocation.items.length === 0) return 0;
      return this.allocation.items.filter(x => x.is_deleted != 1).reduce((acc, x) => acc += Number(x.qty_required), 0);
    },
    totalAvailableQty() {
      if (!this.allocation || this.allocation.items.length === 0) return 0;
      return this.allocation.items.filter(x => x.is_deleted != 1).reduce((output, data) => output += this.extraDataMap[data.inventory_item_id].available_tags.reduce((acc, x) => acc += x.qty, 0), 0);
    },
    totalOrderedQty() {
      if (!this.allocation || this.allocation.items.length === 0) return 0;
      return this.allocation.items.filter(x => x.is_deleted != 1).reduce((output, data) => output += this.extraDataMap[data.inventory_item_id].available_po_items.reduce((acc, x) => acc += x.qty, 0), 0);
    },
    totalAllocatedQty() {
      if (!this.allocation || this.allocation.items.length === 0) return 0;
      return this.allocation.items.filter(x => x.is_deleted != 1).reduce((output, data) => output += this.getAllocatedQty(data), 0);
    },
    totalSuppledQty() {
      if (!this.allocation || this.allocation.items.length === 0) return 0;
      return this.allocation.items.filter(x => x.is_deleted != 1).reduce((output, data) => output += Number(this.extraDataMap[data.inventory_item_id].qty_supplied), 0);
    },
    totalOversupplyReqQty() {
      if (!this.oversupplyItems || this.oversupplyItems.length === 0) return 0;
      return this.oversupplyItems.filter(x => x.is_deleted != 1 && x.request_id).reduce((acc, x) => acc += Number(x.qty_required || 0), 0);
    },
    totalOversuppliedQty() {
      if (!this.oversupplyItems || this.oversupplyItems.length === 0) return 0;
      return this.oversupplyItems.filter(x => x.is_deleted != 1).reduce((acc, x) => acc += Number(x.qty_oversupplied || x.qty_supplied || 0), 0);
    },
    itemUoms() {
      if (!this.newItem.inventory_item_id) return [];
      return this.inventoryItems.find(x => x.id === this.newItem.inventory_item_id)?.uom_items ?? [];
    },
  },
  watch: {
    itemClassId(value) {
      this.filteredSubClassesList = value ? this.classToSubclassesMap[value] : this.subClassesList;
      if (value !== null && value !== 0) this.itemSubclassIds = [];
      this.getInventoryItems();
    },
    itemSubclassIds() {
      this.getInventoryItems();
    }
  },
  created() {
    Promise.all([
      allocationApi.getStatuses(),
      this.$api.color.read(),
      this.$api.uom.getList(),
      this.$api.inventorySubClass.getList(),
      allocationApi.getRequestMaterialStatuses()
    ]).then(([statuses, colors, uoms, subclassesList, requestMaterialStatuses]) => {
      this.colors = colors;
      this.uoms = uoms;
      this.statusMap = statuses.reduce((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {});

      this.subClassesList = subclassesList
      this.classToSubclassesMap = this.subClassesList.reduce((acc, x) => {
        if (!(x.inventory_class_id in acc)) acc[x.inventory_class_id] = [];
        acc[x.inventory_class_id].push(x);
        return acc;
      }, {});
      this.filteredSubClassesList = this.itemClassId ? this.classToSubclassesMap[this.itemClassId] : this.subClassesList;
      this.getInventoryItems();

      this.materialRequestStatusMap = requestMaterialStatuses.reduce((acc, x) => {
        acc[x.id] = x;
        return acc
      }, {});
    })
  },
  methods: {
    async open(allocationId) {
      this.$bus.$emit('loading');
      this.resetForm();
      Promise.all([
        allocationApi.getById(allocationId),
        allocationApi.getAllocationData(allocationId),
      ]).then(([allocation, allocationData]) => {
        this.allocation = allocation;
        this.extraDataMap = allocationData.items;
        this.oversupplyItems = allocationData.extra_items;
        this.showErrorsTable = this.allocation.item_errors?.length;
        this.originalPoSelectionMap = {};

        Object.keys(this.tagQtyAvailableMap).forEach(key => this.tagQtyAvailableMap[key] = 0);
        Object.keys(this.poQtyAvailableMap).forEach(key => this.poQtyAvailableMap[key] = 0);
        Object.keys(this.itemTagQtyAvailableMap).forEach(key => this.itemTagQtyAvailableMap[key] = 0);
        Object.keys(this.itemPoQtyAvailableMap).forEach(key => this.itemPoQtyAvailableMap[key] = 0);

        for (let inventoryItemId in this.extraDataMap) {
          this.extraDataMap[inventoryItemId].available_tags.forEach(tag => {
            if (!(inventoryItemId in this.itemTagQtyAvailableMap)) this.itemTagQtyAvailableMap[inventoryItemId] = 0;
            this.itemTagQtyAvailableMap[inventoryItemId] += tag.qty
            this.tagQtyAvailableMap[tag.inventory_tag] = tag.qty
          });
          this.extraDataMap[inventoryItemId].available_po_items.forEach(po => {
            this.poQtyAvailableMap[po.id] = po.qty;
            if (!(inventoryItemId in this.itemPoQtyAvailableMap)) this.itemPoQtyAvailableMap[inventoryItemId] = 0;
            this.itemPoQtyAvailableMap[inventoryItemId] += po.qty;
          });
          
        }

        this.allocation.items.forEach(item => {
          if (!item.tags) item.tags = [{ id: null, inventory_tag: null, qty: null }];
          else if(item.tags.length === 0) item.tags.push({ id: null, inventory_tag: null, qty: null });
          this.poLoadingRotation[item.inventory_item_id] = 0;

          this.extraDataMap[item.inventory_item_id].available_tags?.forEach(tag => tag.disabled = false);

          item.inventory_po_item_qty = Number(item.inventory_po_item_qty) || null;
          if (item.inventory_po_item_id) {
            this.originalPoSelectionMap[item.id] = { po_id: item.inventory_po_item_id, po_qty: item.inventory_po_item_qty };
          }
          if (!(item.inventory_item_id in this.itemPoQtyAvailableMap)) this.itemPoQtyAvailableMap[item.inventory_item_id] = 0;
          this.itemPoQtyAvailableMap[item.inventory_item_id] += item.inventory_po_item_qty;
          item.tags.forEach(tag => {
            if (tag.qty !== null) {
              tag.qty = Number(tag.qty); //remove decimals
              this.extraDataMap[item.inventory_item_id].qty_allocated -= tag.qty;
              if (!(tag.inventory_tag in this.tagQtyAvailableMap)) this.tagQtyAvailableMap[tag.inventory_tag] = 0;
              if (!(item.inventory_item_id in this.itemTagQtyAvailableMap)) this.itemTagQtyAvailableMap[item.inventory_item_id] = 0;
              this.tagQtyAvailableMap[tag.inventory_tag] += tag.qty;
              this.itemTagQtyAvailableMap[item.inventory_item_id] += tag.qty;
            }
            if (tag.inventory_tag !== null) {
              const tagData = this.extraDataMap[item.inventory_item_id].available_tags?.find(x => x.inventory_tag == tag.inventory_tag);
              if (tagData) tagData.disabled = true;
            }
          });
        });

        this.oversupplyItems.filter(x => x.request_id).forEach(x => {
          if (x.status_id === 1) {
            if (!x.tags) x.tags = [{ id: null, inventory_tag: null, qty: null }];
            else if(x.tags.length === 0) x.tags.push({ id: null, inventory_tag: null, qty: null });
          }

          x.qty_oversupplied = Number(x.qty_oversupplied) || null;
          x.qty_required = Number(x.qty_required) || null;
          x.inventory_po_item_qty = Number(x.inventory_po_item_qty) || null;

          if (x.inventory_po_item_id) {
            this.originalPoSelectionMap[x.request_id] = { po_id: x.inventory_po_item_id, po_qty: x.inventory_po_item_qty };
          }

          x.tags.forEach(tag => {
            if (tag.qty !== null) {
              tag.qty = Number(tag.qty); //remove decimals
              if (!(tag.inventory_tag in this.tagQtyAvailableMap)) this.tagQtyAvailableMap[tag.inventory_tag] = 0;
              this.tagQtyAvailableMap[tag.inventory_tag] += tag.qty;
            }
          });
          this.extraDataMap[x.inventory_item_id].available_tags?.forEach(tag => tag.disabled = false);
          if (x.inventory_item_id) this.poLoadingRotation[x.inventory_item_id] = 0;
        });

        this.processBomChanges();

        this.isInitialized = true;
        this.$bus.$emit('loading-stop');
      });
    },
    async processBomChanges() {
      if (!this.allocation.bom_id) return;
      this.bom = await bomApi.getById(this.allocation.bom_id);
      const allocationItemMap = this.allocation.items.reduce((acc, x) => {
        if (x.bom_item_id) acc[x.bom_item_id] = x;

        return acc;
      }, {})
      this.bomChanges = [];
      this.bom.items.forEach(bomItem => {
        const allocationItem = allocationItemMap[bomItem.id]
        if (!allocationItem && bomItem.is_deleted === 0) {
          const bomChange = { ...bomItem };
          bomChange.bom_is_new = true;
          bomChange.reason = 'New BOM Item';
          bomChange.ignore = false;
          this.bomChanges.push(bomChange)
        } else if (bomItem.is_deleted === 1 && allocationItem && allocationItem.is_deleted === 0) {
          allocationItem.bom_is_deleted = true;
          const bomChange = { ...bomItem };
          bomChange.bom_is_deleted = true;
          bomChange.reason = 'BOM Item has been deleted';
          bomChange.ignore = false;
          this.bomChanges.push(bomChange)
        } else if (bomItem && allocationItem && bomItem.quantity != allocationItem.qty_required) {
          allocationItem.bom_is_changed = true;
          const bomChange = { ...bomItem };
          bomChange.bom_is_changed = true;
          bomChange.reason = `BOM Quantity changed to ${Number(bomItem.quantity)}`;
          bomChange.ignore = false;
          this.bomChanges.push(bomChange)
        }
      })
    },
    async getInventoryItems() {
      const filters = {
        inventory_class_id: this.itemClassId,
        inventory_sub_class_ids: this.itemSubclassIds
      };
      const res = await this.$api.inventoryItem.readData({ filters });
      this.inventoryItems = res.data || [];
    },
    resetForm() {
      this.allocation = null;
      this.bom = null;
      this.extraDataMap = {};
      this.showErrorsTable = false;
      this.oversupplyItems = [];
    },
    close() {
      this.resetForm();
      this.$emit('close');
    },
    getArrayNames(array, key = 'name') {
      if (!array) return '';
      if (typeof(array) === 'string') return array;
      return array.map(x => x[key]).join(', ');
    },
    addQr(allocationItem) {
      allocationItem.tags.push({ id: null, inventory_tag: null, qty: null });
    },
    onClickRemoveQr(allocationItem, tag) {
      if (!tag.id) allocationItem.tags = allocationItem.tags.filter(x => x !== tag);
      else tag.is_deleted = 1;
      
      if (allocationItem.tags.filter(x => !x.is_deleted).length === 0) this.addQr(allocationItem);

      const tagData = this.extraDataMap[allocationItem.inventory_item_id].available_tags?.find(x => x.inventory_tag == (tag.inventory_tag?.inventory_tag ?? tag.inventory_tag));
      if (tagData) tagData.disabled = false;
      else if(tag.tag) {
        tag.disabled = false;  
        tag.shelf = tag.tag?.inventory_location?.name ?? '';
        this.extraDataMap[allocationItem.inventory_item_id].available_tags.push(tag);
      }
    },
    deleteItem(item) {
      if (item.id === null) {
        this.allocation.items = this.allocation.items.filter(x => x !== item);
      } else {
        item.is_deleted = 1;
      }
    },
    getPoQty(allocationItem) {
      const selectedPoItemId = allocationItem.inventory_po_item_id;
      if (!selectedPoItemId) return null;
      const availablePoQty = this.poQtyAvailableMap[selectedPoItemId];
      const qtyAllocated = allocationItem.tags.filter(x => !x.is_deleted).reduce((acc, x) => acc += x.qty, 0);
      const qtyLeftToFill = Math.max(allocationItem.qty_required - qtyAllocated, 0);
      return Math.min(qtyLeftToFill, availablePoQty);
    },
    onPoChange(allocationItem) {
      let key = allocationItem.request_id || allocationItem.id;
      if (key in this.originalPoSelectionMap) {
        delete this.originalPoSelectionMap[key];
      }
      allocationItem.inventory_po_item = null;
      
      allocationItem.tags.filter()
      allocationItem.inventory_po_item_qty = this.getPoQty(allocationItem) || '';
    },
    onPoQtyUpdated(allocationItem) {
      allocationItem.inventory_po_item_qty = Math.floor(Number(allocationItem.inventory_po_item_qty));
    },
    getAvailableQty(item) {
      const inventoryItemId = item.inventory_item_id;
      if (!(inventoryItemId in this.extraDataMap)) return 0;

      const availableQty = this.itemTagQtyAvailableMap[item.inventory_item_id] || 0;
      const consumedQty = item.tags.filter(x => x.is_deleted != 1).reduce((acc, x) => acc += Number(x.qty) || 0, 0);
      return availableQty - consumedQty
    },
    getOrderedQty(item) {
      const qtyAvailable = this.itemPoQtyAvailableMap[item.inventory_item_id] || 0;
      const preallocatedQty = Number(item.inventory_po_item_qty);
      return qtyAvailable - preallocatedQty;
    },
    itemIsFullyAllocated(allocationItem) {
      return this.extraDataMap[allocationItem.inventory_item_id].qty_allocated >= allocationItem.qty_required;
    },
    getMaxTagQuantity(allocationItem, tagIdx = null) {
      //TODO review this
      const requiredQty = allocationItem.qty_required;
      const qtyAllocated = allocationItem.tags.filter(x => x.is_deleted != 1).reduce((acc, x, i) => acc += i !== tagIdx ? x.qty : 0, 0);
      const qtyLeftToFill = requiredQty - qtyAllocated;
      const selectedTag = allocationItem.tags[tagIdx]?.inventory_tag ?? null;
      if (selectedTag === null) return qtyLeftToFill;
      const selectedTagQty = this.extraDataMap[allocationItem.inventory_item_id].available_tags.find(x => x.inventory_tag === selectedTag)?.qty || 0;
      return Math.min(selectedTagQty, qtyLeftToFill);
    },

    onTagQtyChanged(allocationItem, tag) {
      tag.qty = Math.floor(Number(tag.qty));
      this.adjustPoQty(allocationItem);
    },

    onTagChanged(allocationItem, tag) {
      this.extraDataMap[allocationItem.inventory_item_id].available_tags.forEach(availableTag => {
        if (allocationItem.tags.findIndex(x => {
          return (x.inventory_tag?.inventory_tag ?? x.inventory_tag) == (availableTag.inventory_tag) //TODO why do some tags have nested objects?
        }) > -1) {
          availableTag.disabled = true
        } else availableTag.disabled = false
      })
      // let tagData = this.extraDataMap[allocationItem.inventory_item_id].available_tags?.find(x => x.inventory_tag === (tag.inventory_tag?.inventory_tag ?? tag.inventory_tag));
      // if (tagData) tagData.disabled = true;

      if (!tag.qty && tag.inventory_tag) {
        const allocatedQty = allocationItem.tags.filter(x => x.is_deleted !== 1).reduce((acc, x) => acc += Number(x.qty) || 0, 0);

        const qtyLeftToAllocate = Math.max(Number(allocationItem.qty_required) - allocatedQty - Number(allocationItem.inventory_po_item_qty), 0);
        const qtyToAllocate = Math.min(qtyLeftToAllocate, this.tagQtyAvailableMap[tag.inventory_tag?.inventory_tag ?? tag.inventory_tag]);
        tag.qty = qtyToAllocate;
      }
    },

    tagQtyIsValid(tag) {
      return tag.qty >= 0 && (this.tagQtyAvailableMap[tag.inventory_tag?.inventory_tag ?? tag.inventory_tag] || 0) >= tag.qty;
    },

    poQtyIsValid(item) {
      const poId = item.inventory_po_item_id;
      const poQty = item.inventory_po_item_qty;
      const qtyHasntChanged = this.originalPoSelectionMap[item.id || item.request_id]?.po_qty === poQty;
      return qtyHasntChanged || poId === null || (poQty >= 0 && (this.poQtyAvailableMap[poId] || 0) >= poQty);
    },

    openItemForm() {
      this.newItem.qty_required = null;
      this.newItem.inventory_item_id = null;
      this.itemClassId = null;
      this.itemSubclassIds = [];
      this.showItemForm = true;
    },

    closeItemForm() {
      this.newItem.qty_required = null;
      this.newItem.inventory_item_id = null;
      this.newItem.color_id = null;
      this.newItem.uom_id = null;
      this.showItemForm = false;
    },

    async updateBomChangesInAllocation(bomChange) {
      if (!bomChange.bom_is_changed) return;
      const allocationItem = this.allocation.items.find(x => x.bom_item_id === bomChange.id);
      if (!allocationItem) return this.$bus.$emit('showError', 'Failed to update BOM Change in Allocation');

      allocationItem.qty_required = bomChange.quantity;
      allocationItem.bom_is_changed = false;
      bomChange.ignore = 1;
      if (allocationItem.inventory_po_item_qty) {
        this.adjustPoQty(allocationItem);
      }
    },

    async removeBomChangeFromAllocation(bomChange) {
      if (!bomChange.bom_is_deleted) return;
      const allocationItem = this.allocation.items.find(x => x.bom_item_id === bomChange.id);
      if (!allocationItem) return this.$bus.$emit('showError', 'Failed to remove BOM Change from Allocation');

      allocationItem.is_deleted = 1;
      bomChange.ignore = 1;
    },

    async addBomChangeToAllocation(bomChange) {
      if (!bomChange.bom_is_new) return;
      try {
        const data = {
          inventory_item_id: bomChange.inventory_item_id,
          uom_id: bomChange.uom_id,
          color_id: bomChange.uom_id
        };
        const inventoryItemData = await allocationApi.getAllocationDataForInventoryItem(data);
        inventoryItemData.available_tags.forEach(tag => {
          this.tagQtyAvailableMap[tag.inventory_tag] = tag.qty
          if (!(bomChange.inventory_item_id in this.itemTagQtyAvailableMap)) this.itemTagQtyAvailableMap[bomChange.inventory_item_id] = 0;
          this.itemTagQtyAvailableMap[bomChange.inventory_item_id] += tag.qty;
        });
        inventoryItemData.available_po_items.forEach(po => {
          this.poQtyAvailableMap[po.id] = po.qty;
          if (!(bomChange.inventory_item_id in this.itemPoQtyAvailableMap)) this.itemPoQtyAvailableMap[bomChange.inventory_item_id] = 0;
          this.itemPoQtyAvailableMap[bomChange.inventory_item_id] += po.qty;
        });
        this.extraDataMap[bomChange.inventory_item_id] = {
          ...inventoryItemData,
          qty_allocated: 0,
          qty_supplied: 0,
        };

        const newItem = {
          id: null,
          bom_item_id: bomChange.id,
          inventory_item_id: bomChange.inventory_item_id,
          inventory_item: bomChange.inventory_item,
          qty_required: bomChange.quantity,
          inventory_po_item_id: null,
          inventory_po_item_qty: null,
          uom_id: bomChange.uom_id,
          uom: bomChange.uom,
          color_id: bomChange.color_id,
          color: bomChange.color,
          tags: [{ id: null, inventory_tag: null, qty: null }],
        };
        this.allocation.items.push(newItem)

        this.poLoadingRotation[bomChange.inventory_item_id] = 0;
        bomChange.ignore = true;
      } catch (e) {
        console.error(e);
        return this.$bus.$emit('showError', 'Failed to add BOM item');
      }
    },

    async addItem() {
      if (!this.newItem.inventory_item_id) return this.$bus.$emit('showError', 'Please select Inventory Item');
      if (!this.newItem.qty_required) return this.$bus.$emit('showError', 'Please enter Quantity Required');
      if (!this.newItem.uom_id) return this.$bus.$emit('showError', 'Please enter UOM');
      if (this.newItem.inventory_item_id in this.extraDataMap) return this.$bus.$emit('showError', 'This Inventory Item already exists in this Allocation');
  
      try {
        this.itemIsBeingAdded = true;
        const inventoryItemId = this.newItem.inventory_item_id;
        const data = {
          inventory_item_id: this.newItem.inventory_item_id,
          uom_id: this.newItem.uom_id,
          color_id: this.newItem.color_id
        };
        const inventoryItemData = await allocationApi.getAllocationDataForInventoryItem(data);
        inventoryItemData.available_tags.forEach(tag => {
          this.tagQtyAvailableMap[tag.inventory_tag] = tag.qty
          if (!(inventoryItemId in this.itemTagQtyAvailableMap)) this.itemTagQtyAvailableMap[inventoryItemId] = 0;
          this.itemTagQtyAvailableMap[inventoryItemId] += tag.qty;
        });
        inventoryItemData.available_po_items.forEach(po => {
          this.poQtyAvailableMap[po.id] = po.qty;
          if (!(inventoryItemId in this.itemPoQtyAvailableMap)) this.itemPoQtyAvailableMap[inventoryItemId] = 0;
          this.itemPoQtyAvailableMap[inventoryItemId] += po.qty;
        });
        this.extraDataMap[inventoryItemId] = {
          ...inventoryItemData,
          qty_allocated: 0,
          qty_supplied: 0,
        };

        this.allocation.items.push({
          id: null,
          inventory_item_id: inventoryItemId,
          inventory_item: this.inventoryItems.find(x => x.id === this.newItem.inventory_item_id),
          qty_required: this.newItem.qty_required,
          inventory_po_item_id: null,
          inventory_po_item_qty: null,
          uom_id: this.newItem.uom_id,
          uom: this.uoms.find(x => x.id === this.newItem.uom_id) ?? null,
          color_id: this.newItem.color_id,
          color: this.colors.find(x => x.id === this.newItem.color_id) ?? null,
          tags: [{ id: null, inventory_tag: null, qty: null }],
        });

        this.poLoadingRotation[inventoryItemId] = 0;
        this.closeItemForm();
      } catch (e) {
        console.error(e);
        return this.$bus.$emit('showError', `Failed to add an item. ${e.response?.data || ''}`);
      } finally {
        this.itemIsBeingAdded = false;
      }
    },

    openItemShowImageForm(allocationItem) {
      const photoData = this.extraDataMap[allocationItem.inventory_item_id]?.inventory_item_photo;
      if (!photoData) return;
      this.$refs.itemShowImageForm.openDialog(photoData.photo, photoData.mime_type, allocationItem.inventory_item.material_id);
    },

    async submitAllocation() {
      try {
        if (this.allocation.is_issued) return;
        if (!this.allocation.allocate_by_date) return this.$bus.$emit('showError', 'Please enter the Allocate By date');
        
        let quantitiesAreValid = true;
        this.allocation.items.forEach(item => {
          if (item.inventory_po_item_id) {
            if (item.inventory_po_item_qty < 0 || item.inventory_po_item_qty > this.poQtyAvailableMap[item.inventory_po_item_id]) {
              quantitiesAreValid = false;
            }
          }

          item.tags.forEach(tag => {
            if (tag.qty === null) return;
            if (tag.qty < 0) quantitiesAreValid = false;
            if (tag.qty > (this.tagQtyAvailableMap[tag.inventory_tag?.inventory_tag ?? tag.inventory_tag] || 0)) quantitiesAreValid = false;
          });
        });
        if (!quantitiesAreValid) return this.$bus.$emit('showError', 'Invalid quantity entered')

        this.allocation.material_requests = this.oversupplyItems.filter(x => x.request_id);
        await allocationApi.updateAllocation(this.allocation);
        this.$bus.$emit('showSuccess', 'Allocation updated');
        this.close();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to save the Allocation');
      }
    },

    async acknowledgeOversupply(item) {
      const confirm = await this.$refs.confirmForm.open({ title: 'Are you sure you want to acknowledge this oversupply item?' });
      if (!confirm) return;

      const data = { rmc_consumption_id: item.id, allocation_id: this.allocation.id };
      try {
         await allocationApi.acknowledgeOversupply(data);
         item.is_acknowledged = 1;
         this.$bus.$emit('showSuccess', 'Oversupply acknowledged');
      } catch (e) {
        console.error(e)
        this.$bus.$emit('showError', 'Failed to acknowledge oversupply');
      }
    },

    openOversupplyNote(oversupplyRecord) {
      this.oversupplyRecord = oversupplyRecord;
      this.showOversupplyNote = true;
    },
    closeOversupplyNote() {
      this.showOversupplyNote = false;
      this.oversupplyRecord = null;
    },

    redirectToPO() {
      const link = this.$router.resolve({ name: 'InventoryPoView', query: { openPoForm: 'true' } });
      window.open(link.href, '_blank');
    },
    redirectToBom() {
      const link = this.$router.resolve({ name: 'jobs', query: { bomJobId: this.allocation.job_id } });
      window.open(link.href, '_blank');
    },
    async refreshPOItems(item) {
      this.poLoadingRotation[item.inventory_item_id] += 1;
      const res = await allocationApi.getAvailablePOItems(item.inventory_item_id, item.id || null);
      this.extraDataMap[item.inventory_item_id].available_po_items = res;
      res.forEach(po => {
        if (!(po.id in this.poQtyAvailableMap)) {
          this.poQtyAvailableMap[po.id] = po.qty;
        }
      })
    },

    async approveMaterialRequest(materialRequest) {
      try {
        if (materialRequest.status_id !== 1) return;
        const confirm = await this.$refs.confirmForm.open({
          title: `Are you sure that you want to approve this Request?`,
        });

        if (!confirm) return;
        const tags = materialRequest.tags.filter(x => x.inventory_tag)
        const allocatedQty = tags.reduce((acc, x) => acc += Number(x.qty), 0);
        const preallocatedQty = Number(materialRequest.inventory_po_item_qty);
        if (allocatedQty + preallocatedQty !== Number(materialRequest.qty_required)) {
          return this.$bus.$emit('showError', 'Please allocate the required quantity');
        }
        const data = { tags, inventory_po_item_id: materialRequest.inventory_po_item_id, inventory_po_item_qty: materialRequest.inventory_po_item_qty };
        const res = await allocationApi.approveMaterialRequest(materialRequest.id, data);
        materialRequest.tags = res.allocation_item.tags;
        this.$bus.$emit('showSuccess', `Request has been approved`);
        materialRequest.status_id = res.status_id;
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', `Failed to approved Material Request`);
      }
    },

    async declineMaterialRequest(materialRequest) {
      try {
        if (materialRequest.status_id !== 1) return;
        const confirm = await this.$refs.confirmForm.open({
          title: `Are you sure that you want to reject this Request?`,
        });
        if (!confirm) return;
        const res = await allocationApi.declineMaterialRequest(materialRequest.id);
        this.$bus.$emit('showSuccess', `Request has been rejected`);
        materialRequest.status_id = res.status_id;
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', `Failed to reject Material Request`);
      }
    },

    async releaseAllocation() {
      try {
        const confirm = await this.$refs.confirmForm.open({ title: 'Are you sure you want to release this Allocation?' });
        if (!confirm) return;
        await allocationApi.releaseAllocation(this.allocation.id);
        this.allocation.is_locked = 1;
        this.$bus.$emit('showSuccess', 'Allocation has been released');
        this.open(this.allocation.id);
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to release Allocation');
      }
    },
    getTagName(tag) {
      return `${tag.inventory_tag}, ${tag.tag?.inventory_location?.name ?? ''}, Qty: ${this.tagQtyAvailableMap[tag.inventory_tag] || 0}`
    },
    openSupplyOverlay(callback, callbackParams) {
      this.showMenuAllocateBy = false;
      callback(callbackParams);
    },
    openAllocateOverlay(callback, callbackParams) {
      this.showMenuSupplyBy = false;
      callback(callbackParams);
    },
    getAllocatedQty(item) {
      return Number(this.extraDataMap[item.inventory_item_id]?.qty_allocated ?? 0) + item.tags.filter(x => x.is_deleted != 1).reduce((acc, x) => acc += Number(x.qty) || 0, 0)
    },

    getPoReceivedQty(item) {
      const quantityReceived = item.inventory_po_item.good_entrances.filter(x => Number(x.inventory_allocation_item_id) === item.id).reduce((acc, x) => acc += x.quantity, 0);
      return quantityReceived;
    },
    getPoItemStatus(item) {
      if (!item.inventory_po_item_id || !item.inventory_po_item || !item.inventory_po_item.good_entrances || item.inventory_po_item.good_entrances.length === 0) return '';
      const quantityOrdered = item.inventory_po_item_qty;
      // const quantityReceived = item.tags.filter(x => x.inventory_po_item_id === item.inventory_po_item_id).reduce((acc, x) => acc += x.qty, 0);
      const quantityReceived = this.getPoReceivedQty(item);
      if (quantityReceived === 0) return '';
      return (quantityOrdered - quantityReceived) > 0 ? 'Partially Received' : 'Received';
    }
  },
};
</script>

<style>
  tr.footer th {
    border-bottom: thin solid rgba(0,0,0,0.12)
  }


  .v-time-picker-title {
    height: 56px !important;
  }

  .v-time-picker-title__time > * {
    font-size: 50px !important;
  }

  .spin-timer {
    transition: transform 0.3s ease-in-out !important
  }
  
</style>